// ---------------------------------------------------------------------------
// useRetailerLogo — Resolve the selected retailer's logo URL
// ---------------------------------------------------------------------------
// Reads the retailer organization_id from collected_data and looks it up in
// the cached retailer_org options to find the logo_url. Returns null when
// the current step is not a retailer step or no logo is available.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';
import type { StepId } from '@/api/types';
import { useCollectedData } from '@/hooks/useCollectedData';
import { useSessionState } from '@/hooks/useSession';
import { isSafeImageUrl } from '@/utils/url';

/** Step IDs that belong to the retailer flow (after retailer_org selection). */
const RETAILER_STEPS = new Set<StepId>([
  'retailer_main_tariff',
  'retailer_tariff',
  'retailer_signup_date',
  'retailer_properties',
  'retailer_custom_type',
  'retailer_custom_properties',
  'retailer_custom_prices',
]);

/**
 * Returns the selected retailer's logo URL, or null if unavailable.
 * Only resolves for retailer flow steps (after org selection).
 */
export function useRetailerLogo(stepId: StepId | null | undefined): string | null {
  const collectedData = useCollectedData();
  const { optionsCache } = useSessionState();
  const isRetailerStep = !!stepId && RETAILER_STEPS.has(stepId);

  return useMemo(() => {
    if (!isRetailerStep) return null;
    const retailer = collectedData['retailer'];
    if (typeof retailer !== 'object' || retailer === null) return null;
    const orgId = (retailer as Record<string, unknown>)['organization_id'];
    if (typeof orgId !== 'string') return null;
    const orgOptions = optionsCache.get('retailer_org');
    if (!orgOptions) return null;
    const match = orgOptions.options.find((o) => o.id === orgId);
    const url = match?.metadata['logo_url'];
    return isSafeImageUrl(url) ? url : null;
  }, [isRetailerStep, collectedData, optionsCache]);
}
